/**
 * Custom Emotion cache that wraps all Chakra UI / Emotion-generated CSS
 * inside `@layer chakra`, allowing Tailwind utilities to win the cascade
 * without `!important`.
 *
 * The corresponding `@layer chakra;` declaration in `globals.css` establishes
 * layer order so that `chakra` sits below Tailwind's unlayered utilities.
 *
 * References:
 *   - emotion-js/emotion#3134 (community solution)
 *   - MUI PR #45596 (same pattern for Material UI + Tailwind v4)
 *   - siriwatknp/mui6-tailwind4-vite (standalone example)
 */
import createCache, { type StylisPlugin } from '@emotion/cache';
import { prefixer } from 'stylis';

/**
 * Stylis plugin that wraps every root-level CSS rule in `@layer <name> { … }`.
 *
 * Two guards prevent incorrect wrapping:
 *   1. `node.root` — skip non-root nodes. Stylis hoists nested `@media` rules
 *      to the root, but their children retain a `root` reference. Checking
 *      `root` (not `parent`) handles this correctly.
 *   2. `node.type === '@layer'` — skip existing `@layer` rules to prevent
 *      double-wrapping.
 */
const wrapInLayer: (layerName: string) => StylisPlugin =
  (layerName) => (node) => {
    if (node.root) return;
    if (node.type === '@layer') return;

    const child = { ...node, parent: node, root: node };
    Object.assign(node, {
      children: [child],
      length: 6,
      parent: null,
      props: [layerName],
      return: '',
      root: null,
      type: '@layer',
      value: `@layer ${layerName}`,
    });
  };

/**
 * Emotion cache shared by all ChakraProviders in the app.
 *
 * - `key: 'chakra'` — namespaces the generated `<style data-emotion="chakra …">` tags
 * - `stylisPlugins` — replaces the default set, so `prefixer` must be
 *   explicitly included for vendor-prefix support
 */
export const chakraLayerCache = createCache({
  key: 'chakra',
  stylisPlugins: [wrapInLayer('chakra'), prefixer],
});
